<template>
  <div v-if="currDataArray && currDataArray.length > 0" :class="$style.container">
    <HgSkeleton v-if="isLoading" height="180px" :class="$style.loaderChart" />
    <chart
      v-else
      :chartType="'area'"
      :fill="chartFill"
      :isIS24="true"
      :stroke="chartStroke"
      @currentDevelopment="handleCurrentDevelopmentChange"
      @developmentStartTime="handleDevelopmentStartTime"
    />
    <HgSegmentedButton
      v-if="currDataArray && currDataArray.length > 2"
      v-model="selectedRange"
      data-testid="yearRange"
      :class="$style.segmentedButton"
      name="yearRangeFilter"
      :options="yearRangeFilter"
      @update:modelValue="filterData"
    />
    <HgSkeleton v-if="isLoading" height="39px" :class="$style.loaderSummary" />
    <p
      v-else
      :class="$style.summary"
      data-testid="summary"
      v-html="
        t('priceTrends.summary.' + propertyType + '.' + mapCurrentLevel(), [
          getLocationSentence(t, locale as HgSupportedLanguage, {
            includePreposition: false,
          }),
          currDevelopment,
          startTime,
          developmentType,
        ])
      "
    ></p>
  </div>
  <div v-else :class="$style.noDataContainer">
    <span :class="$style.summary" data-testid="noData">{{ t('common.noData') }}</span>
  </div>
</template>
<script setup lang="ts">
import {
  HgSegmentedButton,
  type HgSegmentedOptions,
  HgSkeleton,
  type HgSupportedLanguage,
} from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { type PriceEvolutionDataItem } from '@/models/iazi/queries/getPriceEvolutionResponse';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackPriceTrendYearSelection } from '@/tracking';
import { MapGeoLevel } from '@/types';

import chart from './ChartComponent.vue';

const { getLocationSentence } = storeToRefs(useGeoStore());

const mapStore = useMapStore();
const { data, propertyType, currentLevel, isLoading } = storeToRefs(mapStore);
const { t, locale } = useI18n();
const selectedRange = ref<number>(0);

const currDevelopment = ref<number>(0);
const startTime = ref<number | string>(0);
/* define computed variables */
const currDataArray = computed<Array<PriceEvolutionDataItem> | undefined>(
  () => data.value.priceEvoData?.results?.[propertyType.value],
);
const developmentType = computed<string>(() => {
  return currDevelopment.value >= 0 ? t('priceTrends.increase') : t('priceTrends.decrease');
});
const handleDevelopmentStartTime = (value: number | string) => {
  startTime.value = value;
};
const handleCurrentDevelopmentChange = (value: number) => {
  currDevelopment.value = value;
};
const mapCurrentLevel = (): string => {
  if (currentLevel.value == MapGeoLevel.Country) {
    return 'Switzerland';
  } else if (currentLevel.value == MapGeoLevel.Canton) {
    return 'canton';
  } else {
    return 'muniandPost';
  }
};
const chartFill = {
  type: 'solid',
  opacity: 0.2,
  colors: ['#3FFFC9'],
};
const chartStroke = {
  curve: 'straight',
  width: 2,
  colors: ['#00627A'],
};
const yearRangeFilter = computed<HgSegmentedOptions[]>(() => {
  const responseArray = currDataArray.value;
  const len = responseArray?.length ?? 0;
  switch (len) {
    case 0:
    case 1:
    case 2:
      return [];
    case 3:
      return buildSegmentOptions(1);
    case 4:
    case 5:
      return buildSegmentOptions(2);
    case 6:
    case 7:
      return buildSegmentOptions(3);
    default:
      return buildSegmentOptions(4);
  }
});
/* functions are defined here */
const filterData = async (value: string | number) => {
  selectedRange.value = typeof value === 'string' ? parseFloat(value) : value;
  mapStore.setSelectedFilterRange(selectedRange.value);
  trackPriceTrendYearSelection(selectedRange.value);
};
const buildSegmentOptions = (value: number): Array<HgSegmentedOptions> => {
  let tempObj: HgSegmentedOptions = { value: '', text: '' };
  const segArray: Array<HgSegmentedOptions> = [];
  const useShort = value > 2;
  selectedRange.value = value;
  for (let i = value; i <= value && i > 0; i--) {
    tempObj = {
      value: i,
      text: useShort
        ? `${i} ${t('priceTrends.years').charAt(0)}.`
        : i === 1
          ? `${i} ${t('priceTrends.year')}`
          : `${i} ${t('priceTrends.years')}`,
    };
    segArray.push(tempObj);
  }
  tempObj = {
    value: 0.5,
    text: useShort ? `6 ${t('priceTrends.months').charAt(0)}.` : `6 ${t('priceTrends.months')}`,
  };
  segArray.push(tempObj);
  return segArray;
};
const calculateCurrDevelopment = (responseArray: Array<PriceEvolutionDataItem>) => {
  startTime.value = 0;
  currDevelopment.value = 0;
  currDevelopment.value = responseArray[responseArray.length - 1].evolution;
  const element = responseArray[0];
  if (element.timePeriod % 100 == 12) {
    startTime.value = Math.round(element.timePeriod / 100) + 1;
  } else {
    startTime.value = t('priceTrends.midYear', [Math.round(element.timePeriod / 100)]).toString();
  }
};
watch(
  currDataArray,
  () => {
    if (currDataArray.value && currDataArray.value.length > 1) {
      mapStore.setSelectedFilterRange(0);
      calculateCurrDevelopment(currDataArray.value);
    }
  },
  { immediate: true, deep: true },
);
watch(locale, () => {
  mapStore.setSelectedFilterRange(selectedRange.value);
});
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.container {
  > div[class*='HgFormFieldWrapper_noLabel'] {
    padding-top: 0;
  }
}

.segmentedButton {
  margin-bottom: $space-150;

  [class*='HgSegmentedButton_widget'] {
    padding: 0 5px;
    background-color: $color-grey-40;

    &[class*='HgSegmentedButton_primary'] [class*='HgSegmentedButton_active'] {
      &:not([class*='HgSegmentedButton_disabled']) {
        color: $color-black;
        background-color: $color-white;
        border-radius: 6px;
      }
    }

    [class*='HgSegmentedButton_option'] {
      position: relative;
      padding-top: 2px;
      padding-bottom: 2px;
      margin: 5px 0;
      background-color: inherit;
      border: unset;

      @include caption($color-black);

      & {
        line-height: 1.5;
      }

      &:not([class*='HgSegmentedButton_active'])
        + :not([class*='HgSegmentedButton_active'])::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 0.5px;
        content: '';
        background-color: $color-grey-80;
      }
    }
  }
}

.noDataContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 280px;
}

.summary {
  margin-top: 0;
  margin-bottom: 0;
  color: $color-grey-100;

  @include body-3;
}

.loaderSummary {
  margin-top: $space-50;
  margin-bottom: $space-50;
}

.loaderChart {
  margin-top: $space-75;
  margin-bottom: $space-75;
}
</style>
